import { $service } from '@xt/client/experimental/request/service'
import { hash } from 'ohash'
import $global from '@xt/client/store/global'

import type { BookConfig, PlatformConfigSuffix, ImgRangeOptions } from '@xt/client/types/book.d'
import { EnumApiCurrency } from '@xt/client/api/currency'
import { EnumApiCore } from '@xt/client/api/core'

export enum EumLoadStatus {
  Success = 'success',
  Error = 'error',
  Expired = 'expired'
}

class BookManager {
  private cache: Map<string, BookConfig>

  private bookImgData: Map<string, Map<string, string>> = new Map()

  constructor() {
    this.cache = new Map()
  }

  async getBook(jsonFileName: `${string}-${PlatformConfigSuffix}.json`) {
    if (this.cache.has(jsonFileName)) {
      return this.cache.get(jsonFileName)!
    }

    const [error, dataSource] = await $service<BookConfig>({
      method: 'GET',
      url: `/mocks/${jsonFileName}`,
      baseURL: $global.production ? 'https://static.xuantong.cn' : 'https://testing-static.xuantong.cn'
    })

    if (error) {
      return null
    }

    this.cache.set(jsonFileName, dataSource)
    return dataSource
  }

  // 检查是否有权限查看
  async checkHasAuthView(bookId) {
    const [err, res] = await $service<boolean>({
      url: EnumApiCore.bookAuth,
      method: 'GET',
      data: {
        bookId
      }
    })

    if (err) return false

    return res === true
  }

  // 批量获取
  private async batchMapRealUrl(bookName: string, imgList: string[]): Promise<string[]> {
    if (!imgList.length) return []

    const [err, res] = await $service<string[]>({
      url: EnumApiCurrency.batchGetPrivateUrl,
      method: 'POST',
      data: {
        bookId: 1,
        filePaths: imgList
      }
    })

    if (err) return imgList

    return Array.isArray(res) ? res : []
  }

  /**
   * @param bookName 文件名
   * @param imgList 图片列表
   * @param startKey 起始索引 (组的索引)
   * @param count 获取连续组的数量（包括首组）
   * @param step 每组包含的图片数量
   * @param status 是否过期
   * @return 返回一个数组，包含start到end的图片url，部分数据为空
   * 判断是否有缓存数据，从 bookImgData 获取
   */
  public async getRealUrlByRange(bookName: string, imgList: string[], options: ImgRangeOptions = {}) {
    const { startKey = 0, count = 1, step = 2, status = EumLoadStatus.Success } = options

    // console.log(
    //   `getRealUrlByRange Options 起始索引 (组的索引):${startKey}, 获取连续组的数量:${count}, 每组包含的图片数量:${step}, 是否过期:${status}`
    // )

    const total = imgList.length
    const result: Array<{ key: string; realUrl: string | null }> = imgList.map(item => ({ key: item, realUrl: null }))

    if (total === 0 || startKey < 0 || startKey >= total) {
      return result.map(item => item.realUrl)
    }

    // 每章的优先缓存数据逻辑，获取指定区域的图片链接数据，组 → 转换成图片索引范围
    const start = Math.max(0, startKey - count) * step
    const end = Math.min(total - 1, (startKey + count + 1) * step - 1)
    const rangeKeys = imgList.slice(start, end + 1)

    // console.log('getRealUrlByRange: RangeKeys', startKey, start, end)

    if (!this.bookImgData.has(bookName)) {
      this.bookImgData.set(bookName, new Map())
    }

    const cacheData = this.bookImgData.get(bookName)!
    const needLoadKeys = status === EumLoadStatus.Success ? rangeKeys.filter(key => !cacheData.has(hash(key))) : rangeKeys

    // console.log('getRealUrlByRange: needLoadKeys', needLoadKeys)

    // if (status === EumLoadStatus.Success) {
    //   console.log('getRealUrlByRange: status', needLoadKeys)
    // }

    if (needLoadKeys.length > 0) {
      try {
        const newUrls = await this.batchMapRealUrl(bookName, needLoadKeys)
        needLoadKeys.forEach((key, i) => {
          if (newUrls[i]) {
            cacheData.set(hash(key), newUrls[i])
          }
        })
      } catch (err) {
        console.error('图片加载失败', err)
      }
    }

    result.forEach(item => {
      const cache = cacheData.get(hash(item.key))
      if (cache) {
        item.realUrl = cache
      }
    })

    console.log(
      'getRealUrlByRange: Result',
      result.map(item => ({
        key: hash(item.key),
        value: item.realUrl
      }))
    )

    return result.map(item => item.realUrl)
  }

  // 离开页面，清空缓存
  public cleanData() {
    this.bookImgData.clear()
  }
}

const bookManager = new BookManager()

export default bookManager
